import React, { useCallback, useRef } from 'react';

import { FormattedMessage } from 'react-intl';

import { useFlags } from '@atlaskit/flag/use-flags';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import type { UploadErrorEventPayload } from '@atlaskit/media-picker/types';
import { token } from '@atlaskit/tokens';

import {
	FileStatus,
	type FilesValue,
} from '../../../../../../../../../../common/types/form/form-fields/field-values';
import { useFormRendererActions } from '../../../../../../../../../../controllers/form-renderer-store';
import { messages } from './messages';

export const useOnUploadError: (
	currentFilesValue: FilesValue,
	fieldId: string,
) => (payload: UploadErrorEventPayload) => void = (
	currentFilesValue: FilesValue,
	fieldId: string,
) => {
	const { showFlag } = useFlags();
	const { updateModifiedValue } = useFormRendererActions();

	// Dynamic state that needs to be accessed from onUploadStart need to be stored in a ref.
	// This is due to how the Media components work. The callbacks are cached, so passing
	// in a new onUploadStart callback into Media components will not register the
	// updated callback.
	const currentFilesValueRef = useRef<FilesValue>(currentFilesValue);
	currentFilesValueRef.current = currentFilesValue;

	const onUploadError = useCallback(
		(payload: UploadErrorEventPayload) => {
			showFlag({
				icon: (
					<StatusErrorIcon spacing="spacious" label="" color={token('color.icon.accent.red')} />
				),
				title: <FormattedMessage {...messages.uploadErrorTitle} />,
				description: <FormattedMessage {...messages.uploadErrorDescription} />,
			});

			const updatedFiles = currentFilesValueRef.current.files.filter(
				(formfile) => formfile.identifier.id !== payload.fileId,
			);

			if (updatedFiles.length === 0) {
				updateModifiedValue(fieldId, null);
			} else {
				updateModifiedValue(fieldId, {
					files: updatedFiles,
					fileStatus: FileStatus.UPLOADED,
				});
			}
		},
		[currentFilesValueRef, fieldId, showFlag, updateModifiedValue],
	);

	return onUploadError;
};
